import React, { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import { agentsData, agentLimitMessage } from "Stores/agentsData";

// Cycled under the spinner while the agent is being composed, so the wait feels alive.
const BUILD_STEPS = [
  "Reading your description…",
  "Picking the right trigger…",
  "Composing the steps…",
  "Setting guardrails…",
  "Putting it together…"
];

const EXAMPLES = [
  "Every morning, find VPs of Sales at SaaS companies with 50–500 employees and enroll them in my outreach sequence",
  "When a contact replies to an email, research them and create a follow-up task for me",
  "Import the attendees of a LinkedIn event, find their emails, and enroll them in my sequence"
];

// Guided templates — the default, opinionated way to create the common agents.
const TEMPLATES = [
  {
    key: "icp",
    icon: "🎯",
    name: "Daily ICP Prospector",
    desc: "Finds ICP-matched people on a schedule and enrolls them into a sequence.",
    to: "/agents/new/icp"
  },
  {
    key: "event",
    icon: "⚡",
    name: "Event agent",
    desc: "Reacts the moment something happens to a contact and acts on it.",
    to: "/agents/new/event"
  },
  {
    key: "list",
    icon: "📋",
    name: "Prospect from a list",
    desc: "Pull contacts from a list or saved search, enrich them, and enroll into a sequence.",
    to: "/agents/new/list"
  },
  {
    key: "companies",
    icon: "🏢",
    name: "Companies → people",
    desc: "Find companies with a List Builder saved search, get their people, enrich them, and enroll them.",
    to: "/agents/new/companies"
  },
  {
    key: "lookalike",
    icon: "👯",
    name: "Lookalike companies",
    desc: "Give up to 5 of your best accounts — find companies that look like them, get their people, and enroll them.",
    to: "/agents/new/lookalike"
  },
  {
    key: "linkedin",
    icon: "🔗",
    name: "Import from LinkedIn",
    desc: "Import a Sales Navigator search, a post's engagers, or an event's attendees — then enrich and enroll.",
    to: "/agents/new/linkedin"
  },
  {
    key: "reply",
    icon: "💬",
    name: "Reply router",
    desc: "When a contact replies, route them by sentiment — act on positive replies, stop outreach on negative ones.",
    to: "/agents/new/reply"
  },
  {
    key: "abm",
    icon: "🎯",
    name: "Target accounts (ABM)",
    desc: "Work an account list — find the buying committee at each target account, enrich them, and enroll into a sequence.",
    to: "/agents/new/abm"
  }
];

const NewAgentChooser = () => {
  const history = useHistory();
  const [prompt, setPrompt] = useState("");
  const [focused, setFocused] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [stepIdx, setStepIdx] = useState(0);
  const [ghost, setGhost] = useState("");
  const taRef = useRef(null);

  // Typewriter placeholder: types out each example, pauses, deletes, moves on.
  // Runs continuously but only shown while the field is empty & unfocused.
  useEffect(() => {
    let pi = 0, ci = 0, deleting = false, timer;
    const tick = () => {
      const full = EXAMPLES[pi];
      if (!deleting) {
        ci++;
        setGhost(full.slice(0, ci));
        if (ci >= full.length) { deleting = true; timer = setTimeout(tick, 2200); return; }
        timer = setTimeout(tick, 34);
      } else {
        ci--;
        setGhost(full.slice(0, ci));
        if (ci <= 0) { deleting = false; pi = (pi + 1) % EXAMPLES.length; timer = setTimeout(tick, 450); return; }
        timer = setTimeout(tick, 14);
      }
    };
    timer = setTimeout(tick, 600);
    return () => clearTimeout(timer);
  }, []);

  // The team's agent limit — warn up front when there's no room for another agent.
  const [usage, setUsage] = useState(agentsData.usage);
  useEffect(() => { agentsData.fetchUsage().then(setUsage); }, []);

  // Advance the status caption while composing (stops on the last one).
  useEffect(() => {
    if (!loading) { setStepIdx(0); return; }
    const t = setInterval(() => setStepIdx(i => Math.min(i + 1, BUILD_STEPS.length - 1)), 1600);
    return () => clearInterval(t);
  }, [loading]);

  // Open a template / the canvas — only when the team has room for another agent.
  const start = async to => { if (await agentsData.canHireAgent()) history.push(to); };

  const generate = async () => {
    const p = prompt.trim();
    if (!p || loading) return;
    if (!(await agentsData.canHireAgent())) return; // at the team's agent limit — don't draft one
    setLoading(true);
    setErr("");
    const draft = await agentsData.generateAgent(p);
    setLoading(false);
    // An ICP-prospecting description comes back as the Daily ICP Prospector template, prefilled —
    // that guided form is where the seller profile & ICP review live.
    if (draft) history.push(draft.templateKey === "daily_icp_prospector" ? "/agents/new/icp" : "/agents/new/custom");
    else setErr(agentsData.error || "Couldn't generate that agent. Try rephrasing your description.");
  };

  const showGhost = !prompt && !focused;
  const atLimit = usage && usage.used >= usage.eligible;

  return (
    <div className="ag-page ag-studio">
      <button className="ag-link ag-studio__back" onClick={() => history.push("/agents")}>← Agents</button>
      {atLimit && (
        <div className="ag-limit">
          <i className="material-icons-outlined">info</i>
          <span>{agentLimitMessage(usage)}</span>
        </div>
      )}

      <div className="ag-hero">
        <div className="ag-hero__glow" aria-hidden="true" />
        <div className="ag-hero__eyebrow">✦ Agent Studio</div>
        <h1 className="ag-hero__title">Describe it. <span className="ag-grad">We'll build it.</span></h1>
        <p className="ag-hero__sub">Tell us what you want your agent to do — in plain English — and we'll compose it for you to review.</p>

        <div className="ag-console">
          <div className={`ag-console__ring ${loading ? "is-loading" : ""} ${focused ? "is-focused" : ""}`}>
            {loading ? (
              <div className="ag-genbuild">
                <div className="ag-genbuild__orb"><span /><span /><span /></div>
                <div className="ag-genbuild__msg" key={stepIdx}>{BUILD_STEPS[stepIdx]}</div>
                <div className="ag-genbuild__quote">“{prompt.length > 90 ? prompt.slice(0, 90) + "…" : prompt}”</div>
              </div>
            ) : (
              <div className="ag-console__field">
                {showGhost && (
                  <div className="ag-console__ghost" aria-hidden="true">
                    {ghost}<span className="ag-console__caret" />
                  </div>
                )}
                <textarea
                  ref={taRef}
                  className="ag-console__input"
                  rows={2}
                  value={prompt}
                  onChange={e => setPrompt(e.target.value)}
                  onFocus={() => setFocused(true)}
                  onBlur={() => setFocused(false)}
                  onKeyDown={e => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) generate(); }}
                />
                <button
                  className="ag-console__go"
                  onClick={generate}
                  disabled={!prompt.trim()}
                  title="Build it (⌘↵)"
                >
                  <span className="ag-console__spark">✦</span> Build it
                </button>
              </div>
            )}
          </div>

          {!loading && (
            <div className="ag-console__chips">
              {EXAMPLES.map((ex, i) => (
                <button key={i} className="ag-chip" onClick={() => { setPrompt(ex); if (taRef.current) taRef.current.focus(); }}>
                  {ex.length > 44 ? ex.slice(0, 44) + "…" : ex}
                </button>
              ))}
            </div>
          )}
          {err && <div className="ag-console__err">{err}</div>}
        </div>
      </div>

      <div className="ag-choose__divider"><span>or start from a template</span></div>

      <div className="ag-choose ag-choose--two">
        {TEMPLATES.map(t => (
          <button key={t.key} className="ag-choose__card" onClick={() => start(t.to)}>
            <div className="ag-choose__icon">{t.icon}</div>
            <div className="ag-choose__body">
              <div className="ag-choose__name">{t.name}</div>
              <div className="ag-choose__desc">{t.desc}</div>
            </div>
          </button>
        ))}
      </div>

      {/* Advanced: the freeform flow canvas — full control, deliberately secondary. */}
      <button className="ag-choose__adv" onClick={() => start("/agents/new/custom")}>
        <span className="ag-choose__advicon">🧩</span>
        <span className="ag-choose__advbody">
          <span className="ag-choose__name">
            Build from scratch <span className="ag-advtag">Advanced</span>
          </span>
          <span className="ag-choose__desc">Full control — pick any trigger and compose steps, waits and branches on a canvas.</span>
        </span>
        <span className="ag-choose__arrow">→</span>
      </button>
    </div>
  );
};

export default NewAgentChooser;
